import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { BookmarksStateModel } from './bookmarks.state.model';
import {
  LoadBookmarks,
  LoadBookmarksSuccess,
  LoadOrganizationBookmarks,
  DeleteBookmark,
  DeleteBookmarkSuccess,
  SaveBookmark,
  SaveBookmarkSuccess,
  SelectBookmark,
  SelectBookmarkSuccess,
  SaveBookmarkFail,
  SaveBookmarkThumbnails,
  ResetBookmarks,
  SortBookmarks,
  CheckBookmark,
  SetCheckedBookmarks,
  ExportBookmarks,
  ExportBookmarksSuccess,
  ExportBookmarksError,
  LoadCollectionBookmarks,
  DeleteBookmarkFail,
  DeleteFavorite,
  DeleteFavoriteSuccess,
  DeleteFavoriteFail,
  CopyBookmark,
  CopyBookmarkFailure,
  CopyBookmarkSuccess,
  SetBookmarkText,
  SetSelectedCategory,
  ClearBookmarks,
  UpdateBookmarkCollection,
  UpdateBookmarkCollectionSuccess,
  UpdateBookmarkCollectionError,
  RemoveBookmarkCollectionSuccess,
  AddBookmarkCollectionSuccess,
  RemoveBookmarkCollectionError,
  AddBookmarkCollectionError
} from './bookmarks.actions';

import { finalize, flatMap, filter, tap, map, toArray } from 'rxjs/operators';
import { BookmarksService } from '../bookmarks.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { concat, forkJoin, of, Observable } from 'rxjs';
import { RegisterChapterThumbnails } from 'src/app/story/state/chapters/story.chapters.actions';
import { bookmarkSortRecent } from '../data/bookmark-sort';
import { PublishService } from 'src/app/publish/publish.service';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { CollectionContentModel } from 'src/app/organizations/models/collection.content.model';
import { StoryService } from 'src/app/story/story.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { ExploreService } from 'src/app/explore/explore.service';
import { HttpErrorResponse } from '@angular/common/http';
import { StoryModel } from 'src/app/story/models/story.model';
import { get } from 'lodash';
import { Thumbnail } from 'src/app/shared/thumbnail-utils';
import * as _ from 'lodash';
import { sortModels } from '../models/bookmark-sort.model';

const BookmarksStateDefaults = {
  loading: true,
  exporting: false,
  copying: false,
  saving: false,
  bookmarks: [],
  selectedBookmark: null,
  sort: bookmarkSortRecent,
  checkedBookmarks: [],
  selectedCategory: null
};

@State<BookmarksStateModel>({
  name: 'bookmarks',
  defaults: BookmarksStateDefaults
})
export class BookmarksState {
  @Selector()
  static getBookmarks(state: BookmarksStateModel) {
    const { bookmarks, sort } = state;
    const { ascending, fields } = sort;
    return sortModels(bookmarks, fields, ascending);
  }

  @Selector()
  static getCheckedBookmarks(state: BookmarksStateModel) {
    return state.checkedBookmarks;
  }

  @Selector()
  static getSelectedBookmark(state: BookmarksStateModel) {
    return state.selectedBookmark;
  }

  @Selector()
  static getLoading(state: BookmarksStateModel) {
    return state.loading;
  }

  @Selector()
  static getExporting(state: BookmarksStateModel) {
    return state.exporting;
  }

  @Selector()
  static getSaving(state: BookmarksStateModel) {
    return state.saving;
  }

  @Selector()
  static getCopying(state: BookmarksStateModel) {
    return state.copying;
  }

  @Selector()
  static getSort(state: BookmarksStateModel) {
    return state.sort;
  }

  @Selector()
  static getSelectedCategory(state: BookmarksStateModel) {
    return state.selectedCategory;
  }

  @Action(LoadBookmarks)
  loadBookmarks(context: StateContext<BookmarksStateModel>, action: LoadBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: []
    });

    // load private content (private bookmarks, custom modules, and favorites)
    forkJoin(
      this.bookmarksService.getPrivateBookmarks(action.query),
      this.bookmarksService.getPrivateConditions(),
      this.bookmarksService.getFavorites()
    )
      .pipe(
        finalize(() => { context.patchState({ loading: false }); })
      )
      .subscribe(([bookmarks, conditions, favorites]) => {
        context.dispatch(new LoadBookmarksSuccess([
          ...bookmarks,
          ...conditions,
          ...favorites
        ]));
      });
  }


  @Action(LoadOrganizationBookmarks)
  loadOrganizationBookmarks(context: StateContext<BookmarksStateModel>, { organizationId }: LoadOrganizationBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: []
    });

    // load group content (private bookmarks and custom modules)
    this.bookmarksService.getOrganizationContent(organizationId)
      .pipe(
        finalize(() => { context.patchState({ loading: false }); })
      )
      .subscribe((bookmarks) => {
        context.dispatch(new LoadBookmarksSuccess(bookmarks));
      });
  }

  @Action(LoadCollectionBookmarks)
  loadCollectionBookmarks(context: StateContext<BookmarksStateModel>, action: LoadCollectionBookmarks) {
    context.patchState({
      loading: true,
      bookmarks: [],
      checkedBookmarks: []
    });

    this.organizationsService.getCollectionContent(action.organizationId, action.collectionId)
      .pipe(
        finalize(() => { context.patchState({ loading: false }); })
      )
      .subscribe((result) => {
        context.dispatch(new LoadBookmarksSuccess(result));
      });
  }

  @Action(LoadBookmarksSuccess)
  loadSuccess({ patchState }: StateContext<BookmarksStateModel>, action: LoadBookmarksSuccess) {
    patchState({
      bookmarks: action.result
    });
  }

  @Action(SelectBookmark)
  loadBookmark(context: StateContext<BookmarksStateModel>, action: SelectBookmark) {
    const { id, type } = action;

    const bookmark$ = type === 'bookmark'
      ? this.bookmarksService.getBookmarkModel(id)
      : this.exploreService.getConditionModel(id);

    bookmark$
      .subscribe((bookmark) => {
        context.dispatch(new SelectBookmarkSuccess(bookmark));
      });
  }

  @Action(SelectBookmarkSuccess)
  selectBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: SelectBookmarkSuccess) {
    context.patchState({
      selectedBookmark: action.bookmark
    });
  }

  // Replace existing or Save new bookmark
  @Action(SaveBookmark)
  saveBookmark(context: StateContext<BookmarksStateModel>, action: SaveBookmark) {
    const bookmarkEntry = action.bookmarkEntry;

    // begin save
    context.patchState({ saving: true });

    // requires story
    let serialized: { story: StoryModel, chapterIdMap: any };
    if (action.includeIndex) {
      const labels = this.store.selectSnapshot(StoryLabelsState.getLabels);
      const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
      this.storyService.storeLabelsInChapters(labels, chapters);
      serialized = this.bookmarksService.createBookmarkStory(action.inplace); // Just a serialized module
      bookmarkEntry.index = serialized.story;
    }

    const method = bookmarkEntry.id ? 'update' : 'save';

    this.bookmarksService.saveBookmark(bookmarkEntry)
      .pipe(
        finalize(() => { // end save
          context.patchState({ saving: false });
        })
      )
      .subscribe(
        (bookmark) => {
          // save thumbnails
          if (action.includeIndex) {
            const thumbnails = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
            context.dispatch(new SaveBookmarkThumbnails(method, bookmark.id, bookmarkEntry, thumbnails, serialized.chapterIdMap));
          }
          // add/remove collections
          if (bookmarkEntry.addCollection || bookmarkEntry.removeCollection) {
            context.dispatch(new UpdateBookmarkCollection(bookmark, bookmarkEntry.addCollection, bookmarkEntry.removeCollection));
          }

          const story: StoryModel = serialized ? serialized.story : null;
          context.dispatch(new SaveBookmarkSuccess(bookmark, story, method));
        },
        (err) => {
          context.dispatch(new SaveBookmarkFail(bookmarkEntry));
        });
  }

  @Action(SaveBookmarkSuccess)
  saveBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: SaveBookmarkSuccess) {
    const savedBookmark = action.bookmark;
    const bookmarks = context.getState().bookmarks;

    // patch bookmark entry
    const savedBookmarkIndex = bookmarks.findIndex(bookmark => bookmark.id === savedBookmark.id);
    if (savedBookmarkIndex !== -1) {
      bookmarks[savedBookmarkIndex] = savedBookmark;
      context.patchState({
        bookmarks: [...bookmarks]
      });
    }

    // update selected bookmar if replacing (save)
    if (action.method === 'save') {
      context.patchState({
        selectedBookmark: action.bookmark
      });
    }
  }


  @Action(DeleteBookmark)
  deleteBookmark(context: StateContext<BookmarksStateModel>, action: DeleteBookmark) {
    const bookmarkId = action.bookmarkId;

    this.bookmarksService.deleteBookmark(bookmarkId)
      .subscribe(
        () => {
          context.dispatch(new DeleteBookmarkSuccess(bookmarkId));
        },
        (message) => {
          context.dispatch(new DeleteBookmarkFail(bookmarkId, message));
        });
  }

  @Action(DeleteBookmarkSuccess)
  deleteBookmarkSuccess(context: StateContext<BookmarksStateModel>, action: DeleteBookmarkSuccess) {
    const bookmarks = context.getState().bookmarks
      .filter(bookmark => bookmark.id !== action.bookmarkId);

    context.patchState({
      bookmarks: [...bookmarks]
    });
  }

  @Action(DeleteFavorite)
  deleteFavorite(context: StateContext<BookmarksStateModel>, action: DeleteFavorite) {
    const bookmark = action.bookmark;
    const bookmarkId = bookmark.id;

    // category normalization: 'condition' | 'anatomy' | 'bookmark'
    const contentType = get(bookmark, 'category', 'bookmark');
    // id normalization
    const contentId = contentType === 'bookmark' ? bookmarkId : this.exploreService.toDatabaseContentId(bookmarkId);

    this.bookmarksService.deleteFavorite(contentId, contentType)
      .subscribe(
        () => {
          context.dispatch(new DeleteFavoriteSuccess(bookmarkId));
        },
        (err) => {
          context.dispatch(new DeleteFavoriteFail(bookmarkId));
        });
  }

  @Action(DeleteFavoriteSuccess)
  deleteFavoriteSuccess(context: StateContext<BookmarksStateModel>, action: DeleteFavoriteSuccess) {
    const bookmarks = context.getState().bookmarks
      .filter(bookmark => bookmark.id !== action.bookmarkId);

    context.patchState({
      bookmarks: [...bookmarks]
    });
  }

  @Action(SortBookmarks)
  sortBookmarks(context: StateContext<BookmarksStateModel>, action: SortBookmarks) {
    const bookmarks = context.getState().bookmarks;

    context.patchState({
      sort: action.sort,
      bookmarks: [...bookmarks]
    });
  }

  @Action(ClearBookmarks)
  clearBookmarks(context: StateContext<BookmarksStateModel>, action: ClearBookmarks) {
    context.patchState({
      bookmarks: [],
      checkedBookmarks: []
    });
  }

  @Action(ResetBookmarks)
  resetBookmarks(context: StateContext<BookmarksStateModel>, action: ResetBookmarks) {
    context.setState(BookmarksStateDefaults);
  }

  @Action(CheckBookmark)
  checkBookmark(context: StateContext<BookmarksStateModel>, action: CheckBookmark) {
    let checkedBookmarks = context.getState().checkedBookmarks;
    const exists = checkedBookmarks.some(bookmark => bookmark === action.id);

    if (action.checked && !exists) {
      checkedBookmarks = [...checkedBookmarks, action.id];
    } else if (!action.checked) {
      checkedBookmarks = checkedBookmarks.filter(bookmark => bookmark !== action.id);
    }

    context.patchState({
      checkedBookmarks: checkedBookmarks
    });
  }

  @Action(SetCheckedBookmarks)
  setCheckedBookmarks(context: StateContext<BookmarksStateModel>, action: SetCheckedBookmarks) {
    context.patchState({
      checkedBookmarks: action.ids
    });
  }

  @Action(SaveBookmarkThumbnails)
  saveBookmarkThumbnails(context: StateContext<BookmarksStateModel>, action: SaveBookmarkThumbnails) {
    // chapter id mapping (new bookmark chapters have new ids)
    const chapterIdMap = action.chapterIdMap;

    // thumbnail urls on server are identified by story-id and chapter-id.
    const thumbnails = action.thumbnails;

    // replace/create thumbnails
    const requests$ = this.bookmarksService.saveBookmarkThumbnails(action.bookmarkId, action.bookmarkEntry, thumbnails, chapterIdMap);

    // conditionally delete thumbnails
    if (action.method === 'update') {
      // Delete extra thumbnails. We are saving over an existing bookmark
      const deleteRequests$ = this.bookmarksService.deleteBookmarkThumbnails(action.bookmarkId, action.bookmarkEntry, thumbnails, chapterIdMap);
      for (const deleteRequest$ of deleteRequests$) {
        requests$.push(deleteRequest$);
      }
    }

    forkJoin(requests$).subscribe((results) => {
      const thumbnailLookup = action.bookmarkEntry.index.chapters.map(chapter => {
        const originalStoryId = action.bookmarkId;
        const newChapterId = chapter.id;
        const originalChapterId = newChapterId;
        return { originalStoryId, newChapterId, originalChapterId };
      });
      const _thumbnails = Thumbnail.generateThumbnailUrls(thumbnailLookup);

      // Chapter thumbnails need to be flushed so thumbnails are properly fetched.
      context.dispatch(new RegisterChapterThumbnails(new Map())).subscribe(() => {
        context.dispatch(new RegisterChapterThumbnails(_thumbnails));
      });
    });
  }

  @Action(ExportBookmarks)
  exportBookmarks(context: StateContext<BookmarksStateModel>, action: ExportBookmarks) {   
    const { bookmarks, widgetURL, widgetOptions, offlineEnabled } = action;
    const exportWidgetOptions = widgetOptions || new WidgetOptions();

    context.patchState({ exporting: true });

    this.publishService.exportModels(bookmarks, widgetURL, exportWidgetOptions, null, action.userLanguage, action.userApp, offlineEnabled)
      .pipe(
        finalize(() => {
          context.patchState({ exporting: false });
        })
      )
      .subscribe((success) => {
        if (success) {
          context.dispatch(new ExportBookmarksSuccess(bookmarks));
        } else {
          context.dispatch(new ExportBookmarksError(bookmarks));
        }
      });
  }

  @Action(UpdateBookmarkCollection)
  updateBookmarkCollection(context: StateContext<BookmarksStateModel>, action: UpdateBookmarkCollection) {
    const { bookmark, addCollection, removeCollection } = action;
    const collectionContent: CollectionContentModel = {
      content_type: bookmark.type,
      content_id: bookmark.id
    };

    const operations$ = [];

    // #TODO[AC]: service updates to tidy app side bulk updates

    // important: removal need before adding, if applicable
    if (removeCollection) {
      operations$.push(
        // get bookmark's collection content id
        this.organizationsService.getCollectionContentId(removeCollection, collectionContent)
          .pipe(
            // validate id
            filter((collectionContentId) => !!collectionContentId),
            // remove bookmark via collection content id
            flatMap((collectionContentId) => this.organizationsService.removeFromCollection(removeCollection, [collectionContentId])),
            tap(
              () => context.dispatch(new RemoveBookmarkCollectionSuccess(bookmark, removeCollection)),
              () => context.dispatch(new RemoveBookmarkCollectionError(bookmark, removeCollection))
            )
          )
      );
    }

    // optionally: add to collection
    if (addCollection) {
      operations$.push(
        // add content to specified collection
        this.organizationsService.addToCollection(addCollection, [collectionContent])
          .pipe(
            tap(
              () => context.dispatch(new AddBookmarkCollectionSuccess(bookmark, removeCollection)),
              () => context.dispatch(new AddBookmarkCollectionError(bookmark, removeCollection))
            )
          )
      );
    }

    // execute operations
    concat(
      ...operations$
    )
      .subscribe(
        () => context.dispatch(new UpdateBookmarkCollectionSuccess(bookmark, addCollection, removeCollection)),
        () => context.dispatch(new UpdateBookmarkCollectionError(bookmark, addCollection, removeCollection))
      );
  }

  @Action(RemoveBookmarkCollectionSuccess)
  removeBookmarkCollectionSuccess(context: StateContext<BookmarksStateModel>, action: RemoveBookmarkCollectionSuccess) {
    const { bookmarks, selectedCategory } = context.getState();

    // if current state bookmarks are from collection data: update list
    if (selectedCategory === 'collection') {
      const bookmarkId = action.bookmark.id;
      const patchBookmarks = bookmarks
        .filter(bookmark => bookmark.id !== bookmarkId);

      context.patchState({
        bookmarks: [...patchBookmarks]
      });
    }
  }

  @Action(CopyBookmark)
  copyBookmark(context: StateContext<BookmarksStateModel>, action: CopyBookmark) {
    const { groupAccess, collection } = action;

    context.patchState({ copying: true });
    
    this.bookmarksService.copyBookmark(action.id, action.type) // copy bookmark
      .pipe(
        flatMap((id: string) => {
          const operations$: Observable<any>[] = [of(id)]; // required: bookmark id

          if (groupAccess) { // optional: give group access
            operations$.push(
              this.bookmarksService.saveBookmark({ isOwner: true, id: id, add_group_access: [groupAccess] })
            );
            if (collection) { // optional: add to collection
              operations$.push(
                this.organizationsService.addToCollection(collection, [{ content_type: 'bookmark', content_id: id }])
              );
            }
          }

          return concat(...operations$)
            .pipe(
              toArray(),
              map(() => id)
            );
        }),
        finalize(() => {
          context.patchState({ copying: false });
        })
      )
      .subscribe((id: string) => {
        context.dispatch(new CopyBookmarkSuccess(id, action.id, groupAccess, collection));
      }, (error: HttpErrorResponse) => {
        context.dispatch(new CopyBookmarkFailure(action.id, error));
      });
  }

  @Action(SetBookmarkText)
  setBookmarkText(context: StateContext<BookmarksStateModel>, action: SetBookmarkText) {
    const selectedBookmark = context.getState().selectedBookmark;

    context.patchState({
      selectedBookmark: {
        ...selectedBookmark,
        title: action.title
      }
    });
  }

  @Action(SetSelectedCategory)
  setSelectedCategory(context: StateContext<BookmarksStateModel>, { category }: SetSelectedCategory) {
    context.patchState({
      selectedCategory: category ? category : null
    });
  }

  constructor(
    private store: Store,
    private storyService: StoryService,
    private bookmarksService: BookmarksService,
    private exploreService: ExploreService,
    private publishService: PublishService,
    private organizationsService: OrganizationsService
  ) { }
}
